import { subscribeToResourceEvents } from './sse.prepare-chat'

type EventCallbacks = {
  onProgress?: (message: any) => void
  onError?: (error: Error) => void
  onComplete?: () => void
}

/**
 * Global singleton for managing all SSE connections
 * Ensures only one connection per resource type is active
 */
class GlobalConnectionManager {
  private static instance: GlobalConnectionManager

  // Track connections by resource type and id
  private connections: Map<
    string,
    {
      controller: AbortController
      callbacks: Set<EventCallbacks>
      lastUsed: number
    }
  > = new Map()

  // Protection against rapidly cycling connections
  private pendingConnections: Map<string, Promise<any>> = new Map()
  private connectionTimeouts: Map<string, NodeJS.Timeout> = new Map()
  private closeRequests: Map<string, number> = new Map()

  // Track active SSE connection count
  private activeSSEConnectionCount = 0
  private maxAllowedConnections = 2 // Allow one connection for thread and one for sheet

  private constructor() {
    // Set up a garbage collector to clean up unused connections
    setInterval(this.cleanupConnections.bind(this), 30000)
  }

  public static getInstance(): GlobalConnectionManager {
    if (!GlobalConnectionManager.instance) {
      GlobalConnectionManager.instance = new GlobalConnectionManager()
    }
    return GlobalConnectionManager.instance
  }

  private cleanupConnections() {
    const now = Date.now()
    this.connections.forEach((connection, key) => {
      // If not used in 5 minutes, clean up
      if (now - connection.lastUsed > 300000) {
        connection.controller.abort()
        this.connections.delete(key)
        this.activeSSEConnectionCount = Math.max(
          0,
          this.activeSSEConnectionCount - 1
        )
      }
    })
  }

  /**
   * Get a unique key for a resource
   */
  private getResourceKey(resourceType: string, resourceId: string): string {
    return `${resourceType}:${resourceId}`
  }

  /**
   * Subscribe to a resource with callbacks
   */
  public async subscribe(
    resourceType: string,
    resourceId: string,
    callbacks: EventCallbacks
  ): Promise<() => void> {
    if (!resourceId) {
      console.error(`Cannot subscribe to null/undefined ${resourceType}Id`)
      return () => {} // Return a no-op cleanup
    }

    const resourceKey = this.getResourceKey(resourceType, resourceId)

    // Clear any pending close request for this resource
    if (this.closeRequests.has(resourceKey)) {
      this.closeRequests.delete(resourceKey)
    }

    // Check if we're in a reconnection cooldown period
    if (this.connectionTimeouts.has(resourceKey)) {
      // In development we bypass, but in production we wait
      if (process.env.NODE_ENV === 'development') {
        clearTimeout(this.connectionTimeouts.get(resourceKey)!)
        this.connectionTimeouts.delete(resourceKey)
      } else {
        // Wait for cooldown to expire
        await new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!this.connectionTimeouts.has(resourceKey)) {
              clearInterval(checkInterval)
              resolve(null)
            }
          }, 100)
        })
      }
    }

    // If we already have a connection for this resource, just add the callback
    if (this.connections.has(resourceKey)) {
      const connection = this.connections.get(resourceKey)!
      connection.callbacks.add(callbacks)
      connection.lastUsed = Date.now()
      return () => this.unsubscribe(resourceType, resourceId, callbacks)
    }

    // Check if we're at the connection limit
    if (this.activeSSEConnectionCount >= this.maxAllowedConnections) {
      // Close the oldest connection to make room for the new one
      this.closeOldestConnection()
    }

    // Create a new controller and connection
    const controller = new AbortController()

    // Create new connection entry
    this.connections.set(resourceKey, {
      controller,
      callbacks: new Set([callbacks]),
      lastUsed: Date.now(),
    })

    // Increment connection count
    this.activeSSEConnectionCount++

    // Create a callback that will dispatch to all registered callbacks
    const sharedCallbacks: EventCallbacks = {
      onProgress: (message) => {
        const connection = this.connections.get(resourceKey)
        if (connection) {
          connection.lastUsed = Date.now() // Update last used timestamp
          connection.callbacks.forEach((cb) => {
            cb.onProgress?.(message)
          })
        } else {
          console.warn(
            `No callbacks registered for ${resourceType} ${resourceId} to process event:`,
            message.event
          )
        }
      },
      onError: (error) => {
        const connection = this.connections.get(resourceKey)
        if (connection) {
          connection.callbacks.forEach((cb) => cb.onError?.(error))
        }
      },
      onComplete: () => {
        const connection = this.connections.get(resourceKey)
        if (connection) {
          connection.callbacks.forEach((cb) => cb.onComplete?.())
        }
        // Don't auto-remove on complete - wait for explicit unsubscribe
      },
    }

    // Start the subscription process
    const connectionPromise = (async () => {
      try {
        await subscribeToResourceEvents(
          resourceType,
          resourceId,
          sharedCallbacks,
          controller.signal
        )
      } catch (error) {
        console.error(`Error in ${resourceType} ${resourceId}:`, error)

        // Only delete the connection if it wasn't replaced
        const connection = this.connections.get(resourceKey)
        if (connection && connection.controller === controller) {
          this.connections.delete(resourceKey)
          this.activeSSEConnectionCount = Math.max(
            0,
            this.activeSSEConnectionCount - 1
          )
        }

        throw error
      } finally {
        // Clean up the pending connection reference
        if (this.pendingConnections.get(resourceKey) === connectionPromise) {
          this.pendingConnections.delete(resourceKey)
        }
      }
    })()

    // Store the promise
    this.pendingConnections.set(resourceKey, connectionPromise)

    // Return unsubscribe function
    return () => this.unsubscribe(resourceType, resourceId, callbacks)
  }

  /**
   * Find and close the oldest connection
   */
  private closeOldestConnection(): void {
    let oldestKey: string | null = null
    let oldestTime = Infinity

    this.connections.forEach((connection, key) => {
      if (connection.lastUsed < oldestTime) {
        oldestTime = connection.lastUsed
        oldestKey = key
      }
    })

    if (oldestKey) {
      const [resourceType, resourceId] = oldestKey.split(':')
      this.closeConnection(resourceType, resourceId)
    }
  }

  /**
   * Unsubscribe specific callbacks
   */
  private unsubscribe(
    resourceType: string,
    resourceId: string,
    callbacks: EventCallbacks
  ): void {
    const resourceKey = this.getResourceKey(resourceType, resourceId)
    const connection = this.connections.get(resourceKey)
    if (!connection) return

    // Remove the specific callbacks
    connection.callbacks.delete(callbacks)

    // If no more callbacks, schedule connection for closure after a delay
    // This helps with React's StrictMode double-mounting behavior
    if (connection.callbacks.size === 0) {
      // Mark when we requested the close
      this.closeRequests.set(resourceKey, Date.now())

      // Wait before actually closing
      setTimeout(() => {
        // Only close if there are still no callbacks AND the close request is still active
        const connectionNow = this.connections.get(resourceKey)
        if (
          connectionNow &&
          connectionNow.callbacks.size === 0 &&
          this.closeRequests.has(resourceKey)
        ) {
          connectionNow.controller.abort()
          this.connections.delete(resourceKey)
          this.activeSSEConnectionCount = Math.max(
            0,
            this.activeSSEConnectionCount - 1
          )
          this.closeRequests.delete(resourceKey)
        } else {
          this.closeRequests.delete(resourceKey)
        }
      }, 5000) // 5 second delay before closing
    }
  }

  /**
   * Force close a specific connection
   */
  public closeConnection(resourceType: string, resourceId: string): void {
    const resourceKey = this.getResourceKey(resourceType, resourceId)
    const connection = this.connections.get(resourceKey)

    if (connection) {
      // Shorter cooldown in development mode
      const cooldownTime = process.env.NODE_ENV === 'development' ? 500 : 2000

      // Add to timeout list to prevent immediate reconnection
      const timeout = setTimeout(() => {
        this.connectionTimeouts.delete(resourceKey)
      }, cooldownTime)

      this.connectionTimeouts.set(resourceKey, timeout)

      // Abort and remove connection
      connection.controller.abort()
      this.connections.delete(resourceKey)
      this.activeSSEConnectionCount = Math.max(
        0,
        this.activeSSEConnectionCount - 1
      )

      // Also clear any pending close requests
      this.closeRequests.delete(resourceKey)
    }
  }

  /**
   * Allow forceful connection bypass when needed
   */
  public bypassConnectionCooldown(
    resourceType: string,
    resourceId: string
  ): void {
    const resourceKey = this.getResourceKey(resourceType, resourceId)
    if (this.connectionTimeouts.has(resourceKey)) {
      clearTimeout(this.connectionTimeouts.get(resourceKey)!)
      this.connectionTimeouts.delete(resourceKey)
    }
  }

  /**
   * Close all connections
   */
  public closeAll(): void {
    this.connections.forEach((connection, resourceKey) => {
      connection.controller.abort()
      // Clear any pending close requests
      this.closeRequests.delete(resourceKey)
    })
    this.connections.clear()
    this.activeSSEConnectionCount = 0
  }

  /**
   * Check if a resource is already connected
   */
  public isConnected(resourceType: string, resourceId: string): boolean {
    const resourceKey = this.getResourceKey(resourceType, resourceId)
    return this.connections.has(resourceKey)
  }

  /**
   * Get connection count
   */
  public getConnectionCount(): number {
    return this.activeSSEConnectionCount
  }

  /**
   * Set maximum allowed connections
   */
  public setMaxConnections(max: number): void {
    this.maxAllowedConnections = Math.max(1, max) // Ensure at least 1
  }
}

// Create the global connection manager
const globalConnectionManager = GlobalConnectionManager.getInstance()

// Create simplified resource-specific interfaces that use the global manager
class ResourceConnectionManager {
  private resourceType: string

  constructor(resourceType: string) {
    this.resourceType = resourceType
  }

  public async subscribe(
    resourceId: string,
    callbacks: EventCallbacks
  ): Promise<() => void> {
    return globalConnectionManager.subscribe(
      this.resourceType,
      resourceId,
      callbacks
    )
  }

  public closeResource(resourceId: string): void {
    globalConnectionManager.closeConnection(this.resourceType, resourceId)
  }

  public bypassConnectionCooldown(resourceId: string): void {
    globalConnectionManager.bypassConnectionCooldown(
      this.resourceType,
      resourceId
    )
  }

  public closeAll(): void {
    globalConnectionManager.closeAll()
  }

  public isConnected(resourceId: string): boolean {
    return globalConnectionManager.isConnected(this.resourceType, resourceId)
  }

  public getConnectionCount(): number {
    return globalConnectionManager.getConnectionCount()
  }
}

// Create resource-specific instances that delegate to the global manager
export const connectionManager = new ResourceConnectionManager('project')
// TODO: Remove this hack when we have a better way to handle this
export const projectConnectionManager = new ResourceConnectionManager('project')
// export const sheetConnectionManager = new ResourceConnectionManager('sheet')
// export const fileConnectionManager = new ResourceConnectionManager('file')

// Export the global manager for advanced use cases
export { globalConnectionManager }
